import { useMemo } from "react";
import type { OperationVariables } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  type XYDatum,
  type XYSeriesConfigArray,
  type PieChartConfig,
  type LineData,
  type GeneralBarDatum,
  storageFormatters,
  type PieInnerTextProps,
} from "@jana-technology/platform-ui";

import { REFERENCE_LINE_BASE } from "constants/charts";

import { TopRiskWellsOperationalDocument } from "$/__generated__/graphql";
import { appendORIRData, appendTopRiskWellsSubgroups } from "./utils";

export const useTopRiskWellsByThreatOperational = (
  options?: OperationVariables,
) => {
  const topRiskWellsOperationalResult = useQuery(
    TopRiskWellsOperationalDocument,
    options,
  );

  const memoizedResult = useMemo(() => {
    const riskByThreatsData: Array<GeneralBarDatum> = [];
    const riskByConsequenceData: Array<GeneralBarDatum> = [];
    const riskByOutcomeData: Array<GeneralBarDatum> = [];

    const riskByThreatsDataKeys: Array<string> = [];
    const riskByConsequenceDataKeys: Array<string> = [];
    const riskByOutcomeDataKeys: Array<string> = [];

    const threatsSeriesTotals: XYSeriesConfigArray<"bar", XYDatum> = [];
    const consequenceSeriesTotals: XYSeriesConfigArray<"bar", XYDatum> = [];
    const outcomeSeriesTotals: XYSeriesConfigArray<"bar", XYDatum> = [];

    const topWellsMedianLines: Array<LineData> = [];

    const topWellsPieChartData: Array<PieChartConfig> = [];
    const topWellsPieChartInnerText: Array<PieInnerTextProps> = [];

    if (topRiskWellsOperationalResult.data) {
      const {
        systemMedian,
        selectionMedian,
        operationalSelectionTotal,
        operationalSystemTotal,
        topOperationalTotal,
        total,
        items,
      } = topRiskWellsOperationalResult.data.topRiskWellsSummaryOperational;

      topWellsPieChartData.push({
        id: "system-risk-breakdown",
        data: [
          {
            label: "Top 10 Wells Risk",
            value: topOperationalTotal ?? 0,
          },
          {
            label: "Others in System",
            value: operationalSystemTotal ?? 0,
          },
          {
            label: "Others in Selection",
            value: operationalSelectionTotal ?? 0,
          },
        ],
      });

      topWellsPieChartInnerText.push({
        label: "Total Risk",
        value: storageFormatters.currency(total ?? 0),
      });

      topWellsMedianLines.push(
        {
          id: "top-risk-well-selection-median-line",
          name: "Selection Median",
          yVal: selectionMedian ?? 0,
          ...REFERENCE_LINE_BASE,
        },
        {
          id: "top-risk-well-system-median-line",
          name: "System Median",
          yVal: systemMedian ?? 0,
          ...REFERENCE_LINE_BASE,
          strokeDashoffset: "2",
        },
      );

      for (const item of items) {
        const xBand = `Well ${item.group ?? "unknown"}`;

        const riskByThreatDatum: GeneralBarDatum = {
          x: xBand,
        };

        const riskByConsequenceDatum: GeneralBarDatum = {
          x: xBand,
        };

        const riskByOutcomeDatum: GeneralBarDatum = {
          x: xBand,
        };

        riskByThreatDatum.formatter = (value: number) =>
          storageFormatters.currency(value);
        riskByConsequenceDatum.formatter = (value: number) =>
          storageFormatters.currency(value);
        riskByOutcomeDatum.formatter = (value: number) =>
          storageFormatters.currency(value);

        appendORIRData(
          item.riskWellsByThreats,
          riskByThreatDatum,
          riskByThreatsDataKeys,
        );

        appendORIRData(
          item.riskWellsByConsequence,
          riskByConsequenceDatum,
          riskByConsequenceDataKeys,
        );

        appendORIRData(
          item.riskWellsByOutcomeScenario,
          riskByOutcomeDatum,
          riskByOutcomeDataKeys,
        );

        riskByThreatDatum.tooltipExtras ??= [];
        riskByConsequenceDatum.tooltipExtras ??= [];
        riskByOutcomeDatum.tooltipExtras ??= [];

        appendTopRiskWellsSubgroups(
          riskByConsequenceDatum,
          item.riskWellsByConsequence,
          "operationalTotal",
          "By Category",
        );

        appendTopRiskWellsSubgroups(
          riskByOutcomeDatum,
          item.riskWellsByOutcomeScenario,
          "operationalTotal",
          "Release Outcome",
        );

        riskByThreatDatum.tooltipExtras?.push({
          label: "Total Operational Risk",
          value: storageFormatters.currency(item.operationalTotal ?? 0),
        });

        riskByConsequenceDatum.tooltipExtras?.push({
          label: "Total Operational Risk",
          value: storageFormatters.currency(item.operationalTotal ?? 0),
        });

        riskByOutcomeDatum.tooltipExtras?.push({
          label: "Total Operational Risk",
          value: storageFormatters.currency(item.operationalTotal ?? 0),
        });

        riskByThreatsData.push(riskByThreatDatum);
        riskByConsequenceData.push(riskByConsequenceDatum);
        riskByOutcomeData.push(riskByOutcomeDatum);
      }
    }

    return {
      ...topRiskWellsOperationalResult,
      topWellsMedianLines,
      riskByThreatsData,
      riskByThreatsDataKeys,
      riskByConsequenceData,
      riskByConsequenceDataKeys,
      riskByOutcomeData,
      riskByOutcomeDataKeys,
      threatsSeriesTotals,
      outcomeSeriesTotals,
      consequenceSeriesTotals,
      topWellsPieChartData,
      topWellsPieChartInnerText,
    };
  }, [topRiskWellsOperationalResult]);

  return memoizedResult;
};
